import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { ThemeProvider as IntelieThemeProvider, useThemeContext } from '@intelie/ui'
import i18n from 'live/services/i18n'
import LiveUtils from 'live/lib/utils'
import LiveTooltip from 'live/components/ui/tooltip'
import { WidgetContext } from 'live/context/widget'
import { WidgetEditorContext } from 'live/context/widget-editor'
import { DashboardWidgetContext } from 'live/context/dashboard-widget'
import { DashboardContext } from 'live/context/dashboard'
import dashboardService from 'live/services/dashboard'
import {
    ChannelMetadata,
    ColorStyle,
    CustomSettings,
    CustomSettingsByChannel,
    HIDE_COLOR_VALUE,
    LiveValue,
    LiveMultiValuesMessage,
    LiveMultiValuesState,
    LiveMultiValuesWidgetConfig,
    resolveNewChannelDefaults
} from './api'
import { SettingsModal } from './settings/SettingsModal'
import { ValuesTable } from './ValuesTable'
import { getThresholdBreach, hexToRgba, THRESHOLD_TINT_ALPHA, ThresholdArrow } from './thresholds'
import { useCopyToClipboard } from './useCopyToClipboard'
import { loadViewerOverride, saveViewerOverride, ViewerOverride } from './viewerOverride'

// resolves the effective (custom setting, falling back to channel metadata) thresholds for one channel
const resolveThresholds = (
    metadata: ChannelMetadata,
    settings: CustomSettings | undefined
): Pick<LiveValue, 'minThresholds' | 'maxThresholds' | 'minThresholdColor' | 'maxThresholdColor'> => ({
    minThresholds: settings?.customMinThresholds ?? metadata.minThresholds,
    maxThresholds: settings?.customMaxThresholds ?? metadata.maxThresholds,
    minThresholdColor: settings?.customMinThresholdColor ?? metadata.minThresholdColor,
    maxThresholdColor: settings?.customMaxThresholdColor ?? metadata.maxThresholdColor
})

type ValueBoxProps = {
    value: LiveValue
    boxClassNames: string
    boxMinWidth?: string
    boxDefaultColor?: string
    colorStyle: ColorStyle
    colorTextEnabled: boolean
}

// resolves the color a channel's mark (dot/bar) renders with, falling back through the widget's
// own default when the value/channel doesn't set one - the HIDE_COLOR_VALUE sentinel is left as-is
// here since the mark components already know how to render it as a neutral gray themselves
const resolveMarkColor = (value: LiveValue, boxDefaultColor?: string): string | undefined =>
    value.color ?? boxDefaultColor

// the color to tint the channel title with, when colorTextEnabled is on - null (no tint, falls back
// to the plain title color) when the channel's color is explicitly hidden, since tinting the text
// the same neutral gray the mark renders as would just look like a rendering mistake
const resolveTintColor = (value: LiveValue, boxDefaultColor?: string): string | null => {
    const color = resolveMarkColor(value, boxDefaultColor)

    return color === HIDE_COLOR_VALUE || color == null ? null : color
}

// its own component (rather than rendered inline inside a .map()) so useCopyToClipboard - a hook -
// can be called once per box safely, regardless of how many boxes are in the list
const ValueBox = ({ value, boxClassNames, boxMinWidth, boxDefaultColor, colorStyle, colorTextEnabled }: ValueBoxProps): JSX.Element => {
    const breach = getThresholdBreach(
        value.value,
        value.minThresholds,
        value.maxThresholds,
        value.minThresholdColor,
        value.maxThresholdColor
    )
    const { tooltipContent, handleCopy } = useCopyToClipboard(String(value.value ?? '-'))
    const markColor = resolveMarkColor(value, boxDefaultColor)
    const tintColor = colorTextEnabled ? resolveTintColor(value, boxDefaultColor) : null

    return (
        <Box
            minWidth={boxMinWidth}
            className={boxClassNames}
            style={breach ? { backgroundColor: hexToRgba(breach.color, THRESHOLD_TINT_ALPHA) } : undefined}
        >
            {colorStyle === 'bar' && <BoxColorBar color={markColor} />}
            <BoxTitle className={boxClassNames}>
                {colorStyle === 'circle' && <BoxColorDot color={markColor} />}
                <span style={tintColor ? { color: tintColor } : undefined}>{value.title}</span>
            </BoxTitle>
            <BoxValue className={boxClassNames}>
                <LiveTooltip position="top" content={tooltipContent}>
                    <ValueText onClick={handleCopy}>{value.value ?? '-'}</ValueText>
                </LiveTooltip>
                <ThresholdArrow breach={breach} />
                <BoxUnit className={boxClassNames}>{value.value != null && value.uom ? value.uom : ''}</BoxUnit>
            </BoxValue>
        </Box>
    )
}

interface WidgetContextActions {
    changeConfig?: (field: string, value: unknown, options?: unknown) => void
}

type Props = LiveReactChartProps<LiveMultiValuesState, LiveMultiValuesMessage>

export const LiveMultiValuesWidget = React.memo(function LiveMultiValuesWidget(props: Props): JSX.Element {
    // data from events
    const { values, meta } = props?.state

    // state
    const [showSettingsModal, setShowSettingsModal] = useState(false)

    // contexts
    const dashboardContext = useContext(DashboardContext)
    const dashboardWidgetContext = useContext(DashboardWidgetContext)
    const widgetContext = useContext(WidgetContext)
    const widgetEditorContext = (useContext(WidgetEditorContext) as unknown) as { data: unknown }

    // reactively read the currently active Live theme (light/dark) and inject its --gray-X/--blue-X
    // CSS variables ourselves, rather than assuming an ambient ThemeProvider already covers this
    // widget's DOM subtree - reactWidgetWrapper does not provide one
    const { theme } = useThemeContext()

    const isDashboardEditMode = dashboardContext.state?.params?.mode === 'edit'
    // true specifically inside the widget's own dedicated editor (as opposed to a real dashboard,
    // in view or in layout-edit mode) - see resolveNewChannelDefaults' effect below for why it matters
    const isWidgetEditMode = widgetEditorContext?.data != null

    // layout configurations
    const widgetLayout = dashboardWidgetContext.state.state?.layout
    const deviceEditType = dashboardContext.state?.deviceEditType // doesn't work on edit mode
    const dashboardLayout = dashboardContext.state?.object?.jsonConfig?.layoutModes[deviceEditType] // doesn't work on edit mode
    const widgetOriginalPadding = dashboardService.getPaddingSize(dashboardLayout?.padding ?? 2)

    // canonical widget setup - the one DB-shared config every viewer and the editor read (see api.ts).
    // colorStyle/colorTextEnabled fall back explicitly (rather than relying on WIDGET_DEFAULT_CONFIG
    // alone) since widgets saved before this feature existed have neither field in their persisted
    // jsonConfig at all.
    const {
        boxSize,
        selectedChannels: canonicalSelectedChannels,
        excludedChannels: canonicalExcludedChannels,
        viewMode: canonicalViewMode,
        colorStyle: canonicalColorStyle = 'circle',
        colorTextEnabled: canonicalColorTextEnabled = false
    } = (isDashboardEditMode ? dashboardWidgetContext.state.widget : widgetContext.widget).jsonConfig as LiveMultiValuesWidgetConfig

    // the widget's own stable id - Live's jsonConfig has no per-viewer split for custom plugin
    // fields, so a viewer's own customization is kept browser-local instead, keyed by this id (see
    // viewerOverride.ts). Editor mode never reads/writes it - the editor IS the canonical setup.
    const widgetId = widgetContext?.widget?.id
    const [viewerOverride, setViewerOverride] = useState<ViewerOverride | null>(() =>
        widgetId != null ? loadViewerOverride(widgetId) : null
    )

    useEffect(() => {
        if (widgetId != null) setViewerOverride(loadViewerOverride(widgetId))
    }, [widgetId])

    // effective = what this particular viewer actually sees: their own browser-local override where
    // one exists, the canonical setup otherwise. In edit mode there's no override layer at all - the
    // editor always works directly against the canonical setup, since that's what it's editing.
    const selectedChannels = isWidgetEditMode
        ? canonicalSelectedChannels
        : viewerOverride?.channels?.selectedChannels ?? canonicalSelectedChannels
    const excludedChannels = isWidgetEditMode
        ? canonicalExcludedChannels
        : viewerOverride?.channels?.excludedChannels ?? canonicalExcludedChannels
    const viewMode = isWidgetEditMode ? canonicalViewMode : viewerOverride?.viewMode ?? canonicalViewMode
    const colorStyle = isWidgetEditMode ? canonicalColorStyle : viewerOverride?.colorStyle ?? canonicalColorStyle
    const colorTextEnabled = isWidgetEditMode
        ? canonicalColorTextEnabled
        : viewerOverride?.colorTextEnabled ?? canonicalColorTextEnabled

    // persists a channel-selection change to whichever layer is active: the canonical setup in edit
    // mode (changeConfig, shared by every viewer), or this browser's own override otherwise
    const persistChannels = useCallback(
        (nextSelectedChannels: CustomSettingsByChannel[], nextExcludedChannels: string[]): void => {
            if (isWidgetEditMode) {
                const widgetContextActions = ((widgetContext as unknown) as { actions: WidgetContextActions }).actions
                widgetContextActions.changeConfig('selectedChannels', nextSelectedChannels)
                widgetContextActions.changeConfig('excludedChannels', nextExcludedChannels)
            } else if (widgetId != null) {
                const next: ViewerOverride = {
                    ...viewerOverride,
                    channels: { selectedChannels: nextSelectedChannels, excludedChannels: nextExcludedChannels }
                }
                saveViewerOverride(widgetId, next)
                setViewerOverride(next)
            }
        },
        [isWidgetEditMode, widgetContext, widgetId, viewerOverride]
    )

    // resolves the `selected` @meta default for any channel not already selected and not explicitly
    // removed before - see resolveNewChannelDefaults: a channel with no explicit history is safe to
    // re-evaluate on every update, which is what lets the query drive selection continuously
    const channelDefaultsResolution = useMemo(
        () => resolveNewChannelDefaults(meta.channels, selectedChannels ?? [], excludedChannels ?? []),
        [meta.channels, selectedChannels, excludedChannels]
    )

    useEffect(() => {
        if (!channelDefaultsResolution.changed) return

        persistChannels(channelDefaultsResolution.selectedChannels, excludedChannels ?? [])

        // restart the query so the live_widget_multi_values_identity lookup (populated from selectedChannels
        // by the request interceptor in index.tsx) picks up the newly-added mnemonic - without this the
        // channel would appear but never receive a value, since the running query was started with the
        // old lookup. dashboardContext.actions is only present for a widget embedded in a real
        // dashboard, not the widget's own dedicated editor - optional there, same as in SettingsModal's
        // handleSave, since there's no dashboard query to restart in that context
        const index = ((widgetContext as unknown) as { widgetIndex: number }).widgetIndex
        dashboardContext.actions?.stop?.(index.toString())
        dashboardContext.actions?.start?.(index)
    }, [channelDefaultsResolution, excludedChannels, persistChannels, widgetContext, dashboardContext])

    // disable widget title and timespan automatically
    useEffect(() => {
        const widgetIndex = ((widgetContext as unknown) as { widgetIndex: number }).widgetIndex
        if (widgetLayout?.title) {
            dashboardContext?.actions?.updateWidgetLayoutOptions(widgetIndex, 'title', false)
        }
        if (widgetLayout?.timespan) {
            dashboardContext?.actions?.updateWidgetLayoutOptions(widgetIndex, 'timespan', false)
        }
    }, [dashboardContext, widgetContext, widgetLayout?.title, widgetLayout?.timespan])

    // set css class names: box-small, box-medium, box-large, is-mobile
    const boxClassNames = useMemo(
        () =>
            `box-size-${boxSize?.replace(/[^a-zA-Z0-9]/g, '-') ?? 'small'}${LiveUtils.isMobile() ? ' is-mobile' : ''}`,
        [boxSize]
    )

    // the Settings button is always available, regardless of the channels @meta parameter (but not on mobile) -
    // its Channels tab is only shown when the channels @meta parameter is provided
    const showSettingsButton = useMemo(() => !LiveUtils.isMobile(), [])

    // map for fast reference to channels metadata, filtered by the selected channels
    const channelsMetadataMap = useMemo(
        () =>
            new Map(
                meta.channels
                    ?.filter(channel => selectedChannels?.some(selected => selected.mnemonic === channel.mnemonic))
                    ?.map(channel => [channel.mnemonic, channel]) ?? []
            ),
        [meta.channels, selectedChannels]
    )

    // process values from the last batch
    const processedValues =
        meta.channels?.length > 0
            ? // when the channels @meta parameter is provided, we only show the channels selected in the channels selector
              (selectedChannels ?? [])
                  .map(channel => {
                      const metadata = channelsMetadataMap.get(channel.mnemonic)

                      if (!metadata) return null

                      const value = values.find(value => value.mnemonic === channel.mnemonic)

                      if (value != null) {
                          return {
                              ...value,
                              title: channel.settings?.customTitle ?? metadata.title ?? value.title,
                              color: channel.settings?.customColor ?? metadata.color ?? value.color,
                              ...resolveThresholds(metadata, channel.settings)
                          }
                      }

                      // do not display channel when no value was returned and the channel is marked as transient
                      if (metadata.transient === true) return null

                      // otherwise create a placeholder to keep displaying the box without a value
                      return {
                          timestamp: null,
                          mnemonic: metadata.mnemonic,
                          title: channel.settings?.customTitle ?? metadata.title,
                          layer: -1,
                          uom: null,
                          color: channel.settings?.customColor ?? metadata.color,
                          ...resolveThresholds(metadata, channel.settings)
                      } as LiveValue
                  })
                  .filter(value => value != null)
            : // otherwise, we show exactly the values returned in the last batch for each layer
              values

    const handleHideSettingsModal = useCallback(() => {
        setShowSettingsModal(false)
        // SettingsModal writes a viewer-mode Save directly to localStorage (see viewerOverride.ts) -
        // re-reading it here, right as the modal closes, is what makes this component's own state
        // reflect that write, without needing a callback prop threaded down just for this
        if (!isWidgetEditMode && widgetId != null) setViewerOverride(loadViewerOverride(widgetId))
    }, [isWidgetEditMode, widgetId])

    return (
        <IntelieThemeProvider theme={theme}>
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <AbsoluteContainer widgetOriginalPadding={widgetOriginalPadding}>
                    <WidgetArea>
                        <Header>
                            <Title>{meta.widgetTitle ?? widgetContext?.widget?.title}</Title>
                            {showSettingsButton && (
                                <LiveTooltip position="top" content={i18n('Settings')}>
                                    <HeaderIconButton onClick={() => setShowSettingsModal(true)}>
                                        <i className="fa fa-cog" />
                                        <span>{i18n('Settings')}</span>
                                    </HeaderIconButton>
                                </LiveTooltip>
                            )}
                        </Header>
                        {viewMode === 'table' ? (
                            <ValuesTable
                                values={processedValues}
                                boxDefaultColor={meta.boxDefaultColor}
                                density={boxSize}
                                colorStyle={colorStyle}
                                colorTextEnabled={colorTextEnabled}
                            />
                        ) : (
                            <BoxesContainer className={boxClassNames}>
                                {processedValues?.map(value => (
                                    <ValueBox
                                        key={`${value.layer}-${value.mnemonic ?? value.title}`}
                                        value={value}
                                        boxClassNames={boxClassNames}
                                        boxMinWidth={meta?.boxMinWidth}
                                        boxDefaultColor={meta.boxDefaultColor}
                                        colorStyle={colorStyle}
                                        colorTextEnabled={colorTextEnabled}
                                    />
                                ))}
                            </BoxesContainer>
                        )}
                    </WidgetArea>
                </AbsoluteContainer>
                <SettingsModal
                    show={showSettingsModal}
                    onHide={handleHideSettingsModal}
                    title={meta.widgetTitle ?? widgetContext?.widget?.title}
                    meta={meta}
                    widgetId={widgetId}
                    selectedChannels={selectedChannels}
                    excludedChannels={excludedChannels}
                    viewMode={viewMode}
                    colorStyle={colorStyle}
                    colorTextEnabled={colorTextEnabled}
                    canonicalSelectedChannels={canonicalSelectedChannels}
                    canonicalExcludedChannels={canonicalExcludedChannels}
                    canonicalViewMode={canonicalViewMode}
                    canonicalColorStyle={canonicalColorStyle}
                    canonicalColorTextEnabled={canonicalColorTextEnabled}
                    boxSize={boxSize}
                />
            </div>
        </IntelieThemeProvider>
    )
})

// fill all the widget space (and remove the configured widget padding added by the parent element)
const AbsoluteContainer = styled.div<{ widgetOriginalPadding: number }>`
    position: absolute;
    top: ${({ widgetOriginalPadding }) => `-${widgetOriginalPadding}px`}; // negative padding
    left: ${({ widgetOriginalPadding }) => `-${widgetOriginalPadding}px`}; // negative padding
    height: ${({ widgetOriginalPadding }) => `calc(100% + ${2 * widgetOriginalPadding}px)`};
    width: ${({ widgetOriginalPadding }) => `calc(100% + ${2 * widgetOriginalPadding}px)`};
    background-color: var(--gray-300); // same color as dashboard background to allow rounded borders in the widget area
`

const WidgetArea = styled.div`
    height: 100%;
    width: 100%;
    border-radius: 4px;
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    width: 100%;
    padding: 4px 12px 4px 12px;
    gap: 12px;
    border-radius: 4px 4px 0 0;
    background-color: var(--gray-0);
    box-shadow: 0 10px 30px 2px rgba(0, 0, 0, 0.1);
`

const Title = styled.div`
    flex-grow: 1;
    flex-shrink: 1;
    font-size: 14px;
    line-height: 32px;
    font-weight: 600;
    color: var(--gray-1000);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const HeaderIconButton = styled.div`
    flex-shrink: 0;

    &:hover {
        background-color: var(--gray-200);
        cursor: pointer;
    }

    height: 32px;
    padding: 0 4px 0 8px;
    gap: 8px;
    border-radius: 4px;

    display: flex;
    justify-content: center;
    align-items: center;

    > i {
        font-size: 12px;
        font-weight: 900;
        line-height: 16px;
        color: var(--blue-500); // same accent color as the frac chart tool icons
    }

    > span {
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--blue-500); // follow the icon color
    }
`

const BoxesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    align-content: flex-start;
    gap: 12px;
    overflow-x: hidden;
    overflow-y: scroll;

    &::-webkit-scrollbar-track {
        background-color: transparent;
    }

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgb(180, 180, 180);
    }

    height: calc(100% - 40px); // subtract the Header height
    width: 100%;
    padding: 12px 12px 12px 12px;
    border-radius: 0 0 4px 4px;
    background-color: var(--gray-200);

    &.is-mobile {
        // use horizontal scroll bar
        flex-direction: column;
        overflow-x: scroll;
        overflow-y: hidden;
    }
`

const Box = styled.article<{ minWidth?: string }>`
    position: relative; // anchors BoxColorBar (the "bar" color style's left-edge accent)
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: ${({ minWidth }) => minWidth ?? '120px'};
    min-width: ${({ minWidth }) => minWidth ?? '120px'};
    max-width: 100%;
    width: ${({ minWidth }) => minWidth ?? '120px'};
    height: 51px;
    padding: 6px 14px 6px 10px;
    border-radius: 4px;
    background-color: var(--gray-0);
    box-shadow: 0 10px 30px 2px rgba(0, 0, 0, 0.1);

    &.box-size-medium {
        flex-basis: ${({ minWidth }) => minWidth ?? '140px'};
        min-width: ${({ minWidth }) => minWidth ?? '140px'};
        width: ${({ minWidth }) => minWidth ?? '140px'};
        height: 61px;
        padding: 8px 16px 8px 12px;
    }

    &.box-size-large {
        flex-basis: ${({ minWidth }) => minWidth ?? '160px'};
        min-width: ${({ minWidth }) => minWidth ?? '160px'};
        width: ${({ minWidth }) => minWidth ?? '160px'};
        height: 71px;
        padding: 10px 16px 10px 14px;
    }

    &.is-mobile {
        // we only stretch the boxes horizontally on desktop mode
        flex-grow: 0;
        flex-basis: auto;
    }
`

const BoxTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 18px;
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    color: var(--gray-800);
    overflow: hidden;

    > span:last-child {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &.box-size-medium {
        height: 21px;
        font-size: 14px;
        line-height: 24px;
    }

    &.box-size-large {
        height: 24px;
        font-size: 16px;
        line-height: 24px;
    }
`

// 12px matches the Settings modal's row color mark (both a 25% reduction from the original 16px)
const BoxColorDot = styled.span<{ color?: string }>`
    flex-shrink: 0;
    display: inline-block;
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background-color: ${({ color }) => (color === HIDE_COLOR_VALUE ? 'var(--gray-400)' : color ?? 'var(--gray-400)')};
`

// the "bar" color style's alternative to BoxColorDot - a full-height accent along the box's left
// edge instead of an inline dot next to the title. Rounded only on the left to follow Box's own
// border-radius on that side.
const BoxColorBar = styled.span<{ color?: string }>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    border-radius: 4px 0 0 4px;
    background-color: ${({ color }) => (color === HIDE_COLOR_VALUE ? 'var(--gray-400)' : color ?? 'var(--gray-400)')};
`

// a flex row - only ValueText shrinks/truncates, ThresholdArrow and BoxUnit always stay fully visible
const BoxValue = styled.div`
    display: flex;
    align-items: center;
    height: 21px;
    font-size: 14px;
    line-height: 21px;
    font-weight: 600;
    color: var(--gray-1000);

    &.box-size-medium {
        height: 24px;
        font-size: 16px;
        line-height: 24px;
    }

    &.box-size-large {
        height: 27px;
        font-size: 18px;
        line-height: 27px;
    }
`

const ValueText = styled.span`
    cursor: pointer; // click to copy the full value
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0; // lets a flex child actually shrink/truncate instead of overflowing its row
`

const BoxUnit = styled.span`
    flex-shrink: 0;
    font-size: 10px;
    line-height: 15px;
    font-weight: 400;
    margin-left: 2px;

    &.box-size-medium {
        font-size: 12px;
        line-height: 18px;
    }

    &.box-size-large {
        font-size: 14px;
        line-height: 21px;
    }
`
